"use client";

import {
  Button,
  ButtonIcon,
  Icon,
  Image,
  Link,
  Text,
  View
} from "@/components";
import useWindowSize from "@/hooks/useWindowSize";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMenu } from "./MenuContext";

import type { JSX } from "react";
import type { MenuItem } from "./types";

/**
 * @name SidebarHovered
 * @description Renders a sidebar that expands on hover. Updated to support flat navigation data.
 */
const SidebarHovered = (): JSX.Element => {
  const path = usePathname();
  const { data, sidebarIsOpen, setSidebarIsOpen } = useMenu();
  const { windowSize } = useWindowSize();
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  /**
   * Handles clicks outside the sidebar to close it on smaller screens.
   */
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const currentWindowSize = window.innerWidth;

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        if (currentWindowSize < 1400) {
          setSidebarIsOpen(false);
        }
      }
    },
    [setSidebarIsOpen]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <View
      type="aside"
      ref={sidebarRef}
      className={`group z-[90] ${
        sidebarIsOpen
          ? "translate-x-0 visible opacity-100"
          : "ltr:-translate-x-full rtl:translate-x-full invisible opacity-0"
      } fixed ltr:left-0 rtl:right-0 shadow-lg h-full top-0 will-change-transform dark:bg-sidebar-ring`}
    >
      <View className="flex justify-center items-center">
        <View className="flex justify-between xxl:justify-center group-hover:justify-between items-center w-full px-6">
          <Link href="/" className="flex items-center gap-3">
            <View className="relative w-10 h-10">
              <Image
                width={40}
                height={40}
                src="/icons/icon-96x96.png"
                alt="logo"
                className="object-contain"
              />
            </View>
            <Text className="xxl:hidden group-hover:block text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400">
              Intranet
            </Text>
          </Link>
          <ButtonIcon
            iconLeft={{ name: "bars" }}
            action={() => setSidebarIsOpen(false)}
            className="xxl:hidden text-gray-400"
          />
        </View>
      </View>
      <View className="overflow-y-auto h-[calc(100%-90px)] py-8 px-4">
        <View type="nav" className="flex flex-col gap-2 mt-24">
          {data?.map(({ id, label, icon, value, target, items }: MenuItem) => {
            const isActive = path === String(value);

            if (items && items.length > 0) {
              const isGroupActive = items.some(
                (subItem) => path === String(subItem.value)
              );
              const isGroupOpen = openGroups[id] ?? isGroupActive;

              return (
                <View key={id} className="group/item flex flex-col">
                  <Button
                    action={() => {
                      setOpenGroups((prev) => ({
                        ...prev,
                        [id]: !(prev[id] ?? isGroupActive)
                      }));
                    }}
                    className="group/item flex items-center w-full px-4 py-3 rounded-2xl tracking-wide font-medium whitespace-nowrap transition-[background-color,color,transform] duration-200"
                  >
                    <View className="w-6 h-6 flex justify-center items-center text-lg shrink-0">
                      <Icon name={String(icon)} />
                    </View>
                    <Text className="ml-4 text-base tracking-wide font-medium whitespace-nowrap xxl:hidden group-hover:block text-left">
                      {label}
                    </Text>
                    <View
                      className={`ml-auto text-sm transition-transform duration-200 ${isGroupOpen ? "rotate-180" : ""}`}
                    >
                      <Icon name="chevron-down" />
                    </View>
                  </Button>
                  <View
                    className={
                      isGroupOpen ? "flex flex-col gap-2 mt-2" : "hidden"
                    }
                  >
                    {items.map((subItem) => {
                      const isSubActive = path === String(subItem.value);

                      return (
                        <Link
                          key={subItem.id}
                          href={String(subItem.value)}
                          target={subItem.target ?? "_self"}
                          onClick={() => {
                            if (windowSize && windowSize < 1400)
                              setSidebarIsOpen(false);
                          }}
                          className={`group/item flex items-center px-4 py-3 ml-6 rounded-2xl transition-[background-color,color,transform] duration-200 relative overflow-hidden xxl:justify-center group-hover:justify-start ${isSubActive ? "active" : ""}`}
                        >
                          <View
                            className={`w-6 h-6 flex justify-center items-center text-lg z-10 shrink-0 ${isSubActive ? "active" : ""}`}
                          >
                            <Icon name={`${subItem.icon}`} />
                          </View>
                          <Text
                            className={`ml-4 text-base tracking-wide font-medium whitespace-nowrap xxl:hidden group-hover:block ${isSubActive ? "font-semibold" : ""}`}
                          >
                            {subItem.label}
                          </Text>
                        </Link>
                      );
                    })}
                  </View>
                </View>
              );
            }

            return (
              <Link
                key={id}
                href={String(value)}
                target={target ?? "_self"}
                onClick={() => {
                  if (windowSize && windowSize < 1400) setSidebarIsOpen(false);
                }}
                className={`group/item flex items-center px-4 py-3 rounded-2xl transition-[background-color,color,transform] duration-200 relative overflow-hidden xxl:justify-center group-hover:justify-start ${isActive ? "active" : ""}`}
              >
                <View
                  className={`w-6 h-6 flex justify-center items-center text-lg z-10 shrink-0 ${isActive ? "active" : ""}`}
                >
                  <Icon name={String(icon)} />
                </View>
                <Text
                  className={`ml-4 text-base tracking-wide font-medium whitespace-nowrap xxl:hidden group-hover:block ${isActive ? "font-semibold" : ""}`}
                >
                  {label || ""}
                </Text>
              </Link>
            );
          })}
        </View>
      </View>
    </View>
  );
};

export default SidebarHovered;
